import React, { useState, useEffect } from 'react';
import { Shield, Lock, AlertTriangle, CheckCircle, Key, Fingerprint } from 'lucide-react';

/**
 * Secondary Authentication Component
 * Provides an additional security layer for sensitive dashboards
 * Used as a wrapper for high-security content
 */
const SecondaryAuth = ({ 
  children, 
  dashboardName = 'Sensitive Area',
  securityLevel = 'high',
  onVerified = () => {},
  requirePin = true
}) => {
  const [verified, setVerified] = useState(false);
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [locked, setLocked] = useState(false);
  const [lockTimer, setLockTimer] = useState(0);

  // Check for existing session verification
  useEffect(() => {
    const sessionKey = `secondary_auth_${dashboardName.replace(/\s/g, '_')}`;
    const verifiedAt = sessionStorage.getItem(sessionKey);
    
    if (verifiedAt) {
      const verifiedTime = new Date(verifiedAt).getTime();
      const now = new Date().getTime();
      const sessionDuration = 30 * 60 * 1000; // 30 minutes
      
      if (now - verifiedTime < sessionDuration) {
        setVerified(true);
        onVerified();
      } else {
        sessionStorage.removeItem(sessionKey);
      }
    }
  }, [dashboardName, onVerified]);

  // Handle lockout timer
  useEffect(() => {
    if (locked && lockTimer > 0) {
      const timer = setInterval(() => {
        setLockTimer(prev => {
          if (prev <= 1) {
            setLocked(false);
            setAttempts(0);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [locked, lockTimer]);

  const handlePinSubmit = (e) => {
    e.preventDefault();
    
    if (locked) return;
    
    // Demo PIN: 123456
    const validPins = ['123456', '000000'];
    
    if (validPins.includes(pin)) {
      const sessionKey = `secondary_auth_${dashboardName.replace(/\s/g, '_')}`;
      sessionStorage.setItem(sessionKey, new Date().toISOString());
      setVerified(true);
      setError('');
      onVerified();
    } else {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      setError('Invalid PIN. Please try again.');
      setPin('');
      
      // Lock after 3 failed attempts
      if (newAttempts >= 3) {
        setLocked(true);
        setLockTimer(60); // 60 second lockout
        setError('');
      }
    }
  };

  const handlePinChange = (value) => {
    // Only allow numbers and limit to 6 digits
    const numericValue = value.replace(/\D/g, '').slice(0, 6);
    setPin(numericValue);
    setError('');
  };

  // If already verified, show children
  if (verified) {
    return <>{children}</>;
  }

  // Security verification screen
  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6">
      <div className="max-w-md w-full">
        {/* Security Badge */}
        <div className="text-center mb-8">
          <div className={`w-20 h-20 rounded-2xl mx-auto mb-4 flex items-center justify-center ${
            securityLevel === 'high' ? 'bg-red-100' : 
            securityLevel === 'medium' ? 'bg-amber-100' : 'bg-blue-100'
          }`}>
            <Shield size={36} className={
              securityLevel === 'high' ? 'text-red-600' :
              securityLevel === 'medium' ? 'text-amber-600' : 'text-blue-600'
            } />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Secondary Authentication Required
          </h2>
          <p className="text-slate-500 text-sm">
            Access to <span className="font-medium text-slate-700">{dashboardName}</span> requires additional verification
          </p>
        </div>

        {/* Verification Form */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          {locked ? (
            <div className="text-center py-6">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <Lock size={28} className="text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Account Temporarily Locked</h3>
              <p className="text-slate-500 text-sm mb-4">
                Too many failed attempts. Please wait before trying again.
              </p>
              <div className="text-3xl font-mono font-bold text-red-600">
                {Math.floor(lockTimer / 60)}:{(lockTimer % 60).toString().padStart(2, '0')}
              </div>
            </div>
          ) : (
            <form onSubmit={handlePinSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Enter Security PIN
                </label>
                <div className="relative">
                  <Key size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="password"
                    value={pin}
                    onChange={(e) => handlePinChange(e.target.value)}
                    placeholder="••••••"
                    maxLength={6}
                    className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-center text-2xl tracking-widest font-mono focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                    data-testid="secondary-auth-pin"
                    autoFocus
                  />
                </div>
                <p className="text-xs text-slate-400 mt-2 text-center">
                  6-digit security PIN
                </p>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                  <AlertTriangle size={16} />
                  {error}
                </div>
              )}

              {attempts > 0 && !error && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm text-center">
                  {3 - attempts} attempt(s) remaining
                </div>
              )}

              <button
                type="submit"
                disabled={pin.length < 6}
                className="w-full py-3 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="secondary-auth-submit"
              >
                <Fingerprint size={20} />
                Verify Access
              </button>
            </form>
          )}

          {/* Security Info */}
          <div className="mt-6 pt-6 border-t border-slate-100">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                <Shield size={16} className="text-slate-500" />
              </div>
              <div>
                <p className="text-xs text-slate-500">
                  This area contains sensitive compliance data. Additional verification helps protect against unauthorized access.
                </p>
                <p className="text-xs text-slate-400 mt-2">
                  Demo PIN: <span className="font-mono bg-slate-100 px-1 rounded">123456</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Session Info */}
        <p className="text-xs text-slate-400 text-center mt-4">
          <CheckCircle size={12} className="inline mr-1" />
          Session will remain verified for 30 minutes
        </p>
      </div>
    </div>
  );
};

export default SecondaryAuth;
